import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faAnglesLeft, faAnglesRight } from "@fortawesome/free-solid-svg-icons";
import { config } from "./config";
import "./menu.css";
import { getUser } from "../../helpers/localStorage";

// Preferencia de barra plegada. Se guarda para que se respete al volver.
const COLLAPSE_KEY = "igMenuCollapsed";

const readCollapsed = () => {
  try {
    return window.localStorage.getItem(COLLAPSE_KEY) === "1";
  } catch (e) {
    return false;
  }
};

const writeCollapsed = (value) => {
  try {
    window.localStorage.setItem(COLLAPSE_KEY, value ? "1" : "0");
  } catch (e) {
    /* si el navegador no deja guardar, simplemente no se recuerda */
  }
};

// Iniciales para el avatar: "juan.perez@correo.com" -> "JP"
const getInitials = (name = "") => {
  const limpio = String(name).split("@")[0].replace(/[^a-zA-Z0-9]+/g, " ").trim();
  if (!limpio) return "?";
  const partes = limpio.split(/\s+/);
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[1][0]).toUpperCase();
};

export default function Menu({ username, children }) {
  const [show, setShow] = useState(true);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();

  // Justo despues de iniciar sesion, App.js todavia puede pasar el nombre vacio
  // (lo lee del almacenamiento durante su propio render). Se toma de aqui como
  // respaldo para que la barra nunca salga sin nombre.
  const nombre = username || user.username || "";

  const handleOnclickItem = (item) => {
    if (item.to) {
      navigate(item.to);
      return;
    }

    if (item.action) {
      item.action();
    }
  };

  const mapItems = (items, extraClass = "") => {
    return items.map((item, i) => {
      let visible = true;
      if (item.roles) {
        const hasRole = item.roles.find((role) => role === user.type);
        visible = typeof hasRole !== "undefined";
      }

      if (!visible) return null;

      const activo = item.to === location.pathname;

      return (
        <li key={i} className={extraClass}>
          <button
            type="button"
            className={`ig-menu-link${activo ? " ig-menu-active" : ""}`}
            aria-current={activo ? "page" : undefined}
            aria-label={item.text}
            onClick={() => handleOnclickItem(item)}
          >
            <span className="ig-menu-bar" aria-hidden="true" />
            {item.icon && (
              <span className="ig-menu-ico" aria-hidden="true">
                <FontAwesomeIcon icon={item.icon} />
              </span>
            )}
            <span className="ig-menu-label">{item.text}</span>
            {/* Rotulo flotante: solo se ve cuando la barra esta plegada */}
            <span className="ig-menu-tip" aria-hidden="true">{item.text}</span>
          </button>
        </li>
      );
    });
  };

  return (
    <div className={`ig-menu-screen-wrapper${collapsed ? " ig-menu-is-collapsed" : ""}`}>
      <div className={`ig-menu-side ${show ? "" : "ig-menu-hide"}`}>
        <button
          type="button"
          className="ig-menu-btn-close"
          onClick={() => setShow(false)}
          aria-label="Close menu"
        >
          <span className="ig-menu-icon-cross">X</span>
        </button>

        <button
          type="button"
          className="ig-menu-collapse"
          aria-label={collapsed ? "Expand menu" : "Collapse menu"}
          aria-pressed={collapsed}
          onClick={() => {
            const siguiente = !collapsed;
            setCollapsed(siguiente);
            writeCollapsed(siguiente);
          }}
        >
          <FontAwesomeIcon icon={collapsed ? faAnglesRight : faAnglesLeft} />
          <span className="ig-menu-tip" aria-hidden="true">
            {collapsed ? "Expand menu" : "Collapse menu"}
          </span>
        </button>

        <div className="ig-tittle">
          <span className="ig-menu-avatar" aria-hidden="true" title={nombre}>
            {getInitials(nombre)}
          </span>
          <span className="ig-menu-identity">
            <span className="ig-menu-user" title={nombre}>{nombre}</span>
            <span className="ig-menu-role">Signed in</span>
          </span>
        </div>

        <nav className="ig-menu-nav">
          <ul>{mapItems(config.main)}</ul>
        </nav>

        <ul className="ig-menu-bottom">{mapItems(config.bottom, "ig-menu-danger")}</ul>
      </div>

      <div className="ig-menu-wraper-container">
        <div className="ig-menu-top">
          <div>
            <FontAwesomeIcon icon={faBars} onClick={() => setShow(!show)} />
          </div>
          <div>
            <span>{nombre}</span>
          </div>
        </div>
        <div className="ig-menu-container">{children}</div>
      </div>
    </div>
  );
}
